import { Spinner } from "@/components/ui/spinner";
import { Eyebrow, rowLink } from "@/components/PageUi";
import { cn } from "@/lib/utils";

export interface PanelRow {
  key: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  right?: React.ReactNode;
  /** Colour is kept for status: red for late, green for money in. */
  rightTone?: "red" | "green";
  onOpen?: () => void;
}

export function RowPanel({
  title,
  rows,
  loading,
  empty,
}: {
  title: string;
  rows: PanelRow[];
  loading?: boolean;
  empty: string;
}) {
  return (
    <section className="rounded-xl border bg-card/60 p-4 backdrop-blur-sm">
      <Eyebrow className="mb-2">{title}</Eyebrow>
      {rows.length === 0 ? (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          {loading ? <Spinner className="size-4" /> : null}
          {loading ? "Loading..." : empty}
        </p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => {
            const link = rowLink(row.onOpen);
            return (
              <li
                key={row.key}
                {...link}
                className={cn(
                  "-mx-2 flex items-center justify-between gap-3 rounded-lg border-b border-transparent px-2 py-2 text-sm [&:not(:last-child)]:border-border",
                  row.onOpen && "cursor-pointer transition hover:bg-muted/50",
                  link.className,
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{row.primary}</div>
                    {row.secondary ? (
                      <div className="truncate text-xs text-muted-foreground">{row.secondary}</div>
                    ) : null}
                  </div>
                </div>
                {row.right ? (
                  <span
                    className={cn(
                      "shrink-0 text-sm whitespace-nowrap tabular-nums",
                      row.rightTone === "red"
                        ? "font-medium text-red-600"
                        : row.rightTone === "green"
                          ? "font-medium text-emerald-600"
                          : "font-semibold",
                    )}
                  >
                    {row.right}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
